import { useState, useEffect, useCallback } from "react";
import { useContextSelector } from "use-context-selector";
import AuthContext from "../../context/AuthContext";
import BetSlipContext from "../../context/BetSlipContext";
import { usePolling } from "../../components/usePolling";
import { apiGet } from "../../Utils/apiFetch";
import apiPath from "../../Utils/apiPath";
export const useFavoritesLogic = () => {
  const [list, setList] = useState([]);
  const [position, setPosition] = useState([]);
  const [loader, setLoader] = useState(false);
  const [odds, setOdds] = useState({});
  const [oddsHistory, setOddsHistory] = useState({});
  const [ids, setIds] = useState({ eventId: [], marketId: [] });
  const [active, setActive] = useState([]);

  // Context values
  const betPlace = useContextSelector(BetSlipContext, (ctx) => ctx.betPlace);
  const setLimit = useContextSelector(BetSlipContext, (ctx) => ctx.setLimit);
  const profileData = useContextSelector(AuthContext, (ctx) => ctx.profileData);
  const user = useContextSelector(AuthContext, (ctx) => ctx.user);
  const defaultStakeLimit = useContextSelector(
    AuthContext,
    (ctx) => ctx.defaultStakeLimit
  );
  const setBetSlipObject = useContextSelector(
    BetSlipContext,
    (ctx) => ctx.setBetSlipObject
  );
  const setShowBetDialog = useContextSelector(
    BetSlipContext,
    (ctx) => ctx.setShowBetDialog
  );
  const setToastObject = useContextSelector(
    BetSlipContext,
    (ctx) => ctx.setToastObject
  );
  const betSlipObject = useContextSelector(
    BetSlipContext,
    (ctx) => ctx.betSlipObject
  );
  const showBetDialog = useContextSelector(
    BetSlipContext,
    (ctx) => ctx.showBetDialog
  );

  /** ---------- Helpers ---------- **/
  const transformData = (data) =>
    data.map((item) => ({
      _id: item._id,
      eventId: item.eventId,
      Odds: item.Odds.map((odds) => odds.marketId),
      Bookmaker: true,
      Fancy: true,
      SportBook: true,
    }));

  const handleActive = (eventId, marketId, type) => {
    setActive((prev) => {
      const updated = [...prev];
      const eventIndex = updated.findIndex((e) => e.eventId === eventId);

      if (eventIndex === -1) {
        updated.push({
          _id: eventId,
          eventId,
          Odds: type === "Odds" ? [marketId] : [],
          Bookmaker: type === "Bookmaker",
          Fancy: type === "Fancy",
          SportBook: type === "SportBook",
        });
      } else {
        const event = { ...updated[eventIndex] };

        if (type === "Odds") {
          event.Odds = event.Odds.includes(marketId)
            ? event.Odds.filter((id) => id !== marketId)
            : [...event.Odds, marketId];
        } else {
          event[type] = !event[type];
        }
        updated[eventIndex] = event;
      }
      return updated;
    });
  };

  /** ---------- API Calls ---------- **/
  const getBetPosition = async (marketIds, eventIds) => {
    const { status, data } = await apiGet(
      `${
        apiPath.betPosition
      }?eventIds=${eventIds}&multi=true&marketId=${marketIds?.join(
        ","
      )}&isFavorite=true`
    );
    if (status === 200) setPosition(data?.results || []);
  };

  const getData = async () => {
    setLoader(true);
    const { status, data } = await apiGet(apiPath.favourtieList);

    if (data?.success) {  
      const result = data?.results
        ?.sort((a, b) => new Date(a.eventDateTime) - new Date(b.eventDateTime))
        ?.filter(
          (res) =>
            res?.Odds?.length > 0 ||
            res?.Bookmaker?.length > 0 ||
            res?.Fancy?.length > 0 ||
            res?.SportBook?.length > 0
        );

      setList(result);

      const eventIds = [];
      const marketIds = [];
      data?.results.forEach((item) => {
        if (Array.isArray(item.Odds) && item.Odds.length > 0) {
          item.Odds.forEach((odds) => {
            eventIds.push(item.eventId);
            marketIds.push(odds.marketId);
          });
        } else {
          eventIds.push(item.eventId);
          marketIds.push(item.marketId);
        }
      });

      setIds({ eventId: eventIds, marketId: marketIds });
      setActive(transformData(result));
      if (eventIds?.length) getBetPosition(marketIds, eventIds);
    }
    setLoader(false);
  };

  const getMatchOddsMulti = async (eventIds, marketIds) => {
    try {
      const { status, data } = await apiGet(
        `${apiPath.getMatchOddsNew}?eventId=${eventIds?.join(
          ","
        )}&marketId=${marketIds?.join(",")}`
      );

      if (status === 200 && data.success) {
        const socketData = data?.results;

        setOdds((prevOdds) => {
          const updatedOdds = { ...prevOdds };

          Object.keys(socketData).forEach((eventId) => {
            const newMarkets = socketData[eventId];

            setOddsHistory((prevHistory) => {
              const updatedHistory = { ...prevHistory };
              if (!updatedHistory[eventId]) updatedHistory[eventId] = [];

              if (prevOdds[eventId]) {
                updatedHistory[eventId] = [
                  ...updatedHistory[eventId],
                  {
                    timestamp: new Date().toISOString(),
                    data: prevOdds[eventId],
                  },
                ];
              }

              return updatedHistory;
            });

            updatedOdds[eventId] = newMarkets;
          });

          return updatedOdds;
        });
      }
    } catch (error) {
      console.error("Error fetching match odds:", error);
    }
  };

  const addToFavourite = async (
    id,
    favoriteType,
    favoriteMarketId,
    eventType,
    market
  ) => {
    const params = new URLSearchParams();
    if (id) params.append("eventId", id);
    if (favoriteType) params.append("favoriteType", favoriteType);
    if (favoriteMarketId) params.append("favoriteMarketId", favoriteMarketId);
    if (eventType) params.append("eventType", eventType);
    if (market) params.append("market", market);

    const { data } = await apiGet(
      `${apiPath.addToFavourite}?${params.toString()}`
    );
    if (data?.success) getData();
  };

  /** ---------- Polling ---------- **/
  const fetchOdds = useCallback(async () => {
    if (ids?.eventId?.length && ids?.marketId?.length) {
      await getMatchOddsMulti(ids?.eventId, ids?.marketId);
    }
  }, [ids]);

  usePolling(
    fetchOdds,
    5000,
    ids?.eventId?.length > 0 && ids?.marketId?.length > 0
  );

  /** ---------- Effects ---------- **/
  useEffect(() => {
    if (betPlace?.isPlaced && betPlace?.type === "matched") {
      getBetPosition(ids?.marketId, ids?.eventId);
    }
  }, [betPlace, ids]);

  useEffect(() => {
    getData();
  }, []);

  return {
    list,
    position,
    loader,
    odds,
    oddsHistory,
    ids,
    active,
    handleActive,
    addToFavourite,
    profileData,
    user,
    defaultStakeLimit,
    setLimit,
    betSlipObject,
    setBetSlipObject,
    showBetDialog,
    setShowBetDialog,
    setToastObject,
  };
};
